// Polyfills for modern JS APIs that pdfjs-dist (bundled via react-pdf) calls on
// the main thread. pdfjs 5.x's non-legacy build targets very recent browsers and
// uses `URL.parse()` (Chrome 126+, Safari 18.4+) and `Promise.withResolvers()`
// (Chrome 119+, Safari 17.4+). The Sentry events are overwhelmingly real users,
// not bots: mostly Safari/iOS WebViews below those versions (incl. current-ish
// Safari 17.x and Google-app in-app browsers), with only a couple of stray
// HeadlessChrome hits. They see "URL.parse is not a function" / "undefined is
// not a function" when opening a PDF preview (Sentry WEB-CLIENT-24J,
// WEB-CLIENT-24H, WEB-CLIENT-1K1).
//
// Imported first in index.tsx so the globals exist before pdfjs ever runs.

if (typeof (URL as { parse?: unknown }).parse !== 'function') {
  // Spec: returns null instead of throwing when the input can't be parsed.
  (
    URL as unknown as { parse: (url: string, base?: string) => URL | null }
  ).parse = (url, base) => {
    try {
      return new URL(url, base);
    } catch {
      return null;
    }
  };
}

// Array.prototype.findLast / findLastIndex (Chrome 97+, Safari 15.4+). Old
// in-app WebViews (e.g. Android WebView 95) lack them, so any code that calls
// findLast throws "findLast is not a function" and takes down the surrounding
// feature. Both @tiptap/core and our own code (MonthView getMonthViewData,
// magic message.lib) call findLast, so the editor crashes on those browsers
// (Sentry WEB-CLIENT-26M).
if (typeof Array.prototype.findLast !== 'function') {
  Object.defineProperty(Array.prototype, 'findLast', {
    configurable: true,
    writable: true,
    value: function <T>(
      this: T[],
      predicate: (value: T, index: number, array: T[]) => unknown,
      thisArg?: unknown
    ): T | undefined {
      for (let i = this.length - 1; i >= 0; i--) {
        if (predicate.call(thisArg, this[i], i, this)) {
          return this[i];
        }
      }
      return undefined;
    },
  });
}

if (typeof Array.prototype.findLastIndex !== 'function') {
  Object.defineProperty(Array.prototype, 'findLastIndex', {
    configurable: true,
    writable: true,
    value: function <T>(
      this: T[],
      predicate: (value: T, index: number, array: T[]) => unknown,
      thisArg?: unknown
    ): number {
      for (let i = this.length - 1; i >= 0; i--) {
        if (predicate.call(thisArg, this[i], i, this)) {
          return i;
        }
      }
      return -1;
    },
  });
}

if (
  typeof (Promise as { withResolvers?: unknown }).withResolvers !== 'function'
) {
  (
    Promise as unknown as {
      withResolvers: <T>() => {
        promise: Promise<T>;
        resolve: (value: T | PromiseLike<T>) => void;
        reject: (reason?: unknown) => void;
      };
    }
  ).withResolvers = <T>() => {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

type LocalStorageHost = { localStorage: Storage };

// Some locked-down browsers (notably iOS Safari with website data / cross-site
// tracking blocked, plus sandboxed and third-party contexts) throw a
// SecurityError merely on *accessing* `window.localStorage`. ConfigCat's SDK
// reads `typeof localStorage` outside a try/catch while initializing during
// render, so that access throws and takes the whole app down to the root
// ErrorBoundary before it can render anything (Sentry WEB-CLIENT-1HW).
//
// Install a best-effort in-memory shim, but only when the native accessor
// already throws — normal sessions keep their real localStorage untouched. In a
// broken-storage session an in-memory store can only help: third-party SDKs
// (ConfigCat, analytics) get to feature-detect and run without persistence
// instead of crashing the page. Runs from polyfills so the shim exists before
// ConfigCat ever mounts.
export const ensureLocalStorageAccessible = (
  host: LocalStorageHost = window
): void => {
  try {
    // Touch it the way SDKs do; a throw here means storage access is blocked.
    void host.localStorage;
    return;
  } catch {
    // Storage access is blocked — fall through and install the shim.
  }

  try {
    const store = new Map<string, string>();
    const memoryStorage: Storage = {
      get length() {
        return store.size;
      },
      clear: () => store.clear(),
      getItem: (key) => (store.has(key) ? (store.get(key) as string) : null),
      key: (index) => Array.from(store.keys())[index] ?? null,
      removeItem: (key) => {
        store.delete(key);
      },
      setItem: (key, value) => {
        store.set(key, String(value));
      },
    };
    Object.defineProperty(host, 'localStorage', {
      configurable: true,
      get: () => memoryStorage,
    });
  } catch {
    // The accessor can't be redefined in this browser — a polyfill must never
    // throw, so there is nothing more we can safely do.
  }
};

ensureLocalStorageAccessible();
